import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, describeError } from "@/lib/api";
import { LoadError } from "@/pages/marketplace/MyOrders";
import { useAuth } from "@/context/AuthContext";
import { ImageUploader } from "@/pages/Publish";
import { Store, Plus, Package, Receipt, Pencil, Send, Archive, Info, Wrench, FileText } from "lucide-react";
import { money, StatusPill, SELLER_STATUS_LABEL } from "@/components/marketplace/shared";
import { StripeConnectCard, SalesList } from "@/pages/marketplace/Sales";
import { SubscriptionCard } from "@/pages/marketplace/Subscription";
import { PlansLink } from "@/components/marketplace/PlansLink";
import { SellerQuotes } from "@/pages/marketplace/Quotes";
import { VerificationCard } from "@/pages/marketplace/Verification";
import { SellerChecklist, useOnboarding } from "@/components/Onboarding";
import { RestaurantSettings, MenuEditor, RestaurantReservations } from "@/pages/marketplace/RestaurantSeller";
import { Utensils, Star, Compass, BarChart3 } from "lucide-react";
import { SellerReviews } from "@/components/marketplace/SellerReviews";
import { DirectoryForm } from "@/components/directory/DirectoryForm";
import { SellerInsights } from "@/components/insights/SellerInsights";

const SERVICE_STATUS_LABEL = { draft: "Rascunho", published: "Publicado", paused: "Pausado", archived: "Arquivado" };

const inputCls = "w-full px-3 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm outline-none focus:border-black";

export default function Sell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [seller, setSeller] = useState(undefined);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [tab, setTab] = useState(params.get("tab") || "products");
  useEffect(() => {
    const t = params.get("tab");
    if (!t) return;
    setTab(t);
    setTimeout(() => document.querySelector(`[data-testid="seller-tab-${t}"]`)?.scrollIntoView({ block: "start", behavior: "smooth" }), 50);
  }, [params]);
  const [sales, setSales] = useState([]);
  const [subActive, setSubActive] = useState(false);
  const { start: startTour } = useOnboarding();
  useEffect(() => { if (seller?.status === "approved") { if (tab === "quotes") startTour("seller_quotes"); if (tab === "sales" && !seller?.stripe_transfers_active) startTour("connect"); } }, [tab, seller, startTour]);

  const [loadErr, setLoadErr] = useState(null);
  const load = async () => {
    setLoadErr(null);
    let data;
    try { ({ data } = await api.get("/sellers/me")); }
    catch (e) { setLoadErr(describeError(e, "Não foi possível carregar a tua loja.")); return; }
    setSeller(data);
    if (data?.status === "approved") {
      const [p, s, sv] = await Promise.all([api.get("/products/mine").catch(() => ({ data: [] })), api.get("/orders/sales").catch(() => ({ data: null })), api.get("/services/mine").catch(() => ({ data: [] }))]);
      setProducts(Array.isArray(p.data) ? p.data : []); setSales(Array.isArray(s.data) ? s.data : null); setServices(Array.isArray(sv.data) ? sv.data : []);
    }
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);
  useEffect(() => {
    if (params.get("stripe") === "return") { setTab("sales"); toast.success("Voltaste do Stripe — a verificar estado da conta"); }
    if (params.get("stripe") === "refresh") { setTab("sales"); toast.info("A ligação Stripe expirou — clica em «Continuar configuração Stripe» para gerar um novo link"); }
    const sid = params.get("session_id");
    if (params.get("sub") === "success" && sid) {
      let tries = 0;
      const poll = async () => {
        try { const { data } = await api.get(`/subscriptions/checkout/status/${sid}`); if (data.active) { toast.success("Subscrição activa! Bem-vindo, lojista."); setSubActive(true); return; } } catch {}
        if (++tries < 6) setTimeout(poll, 2000);
      };
      poll();
    }
    /* eslint-disable-next-line */ }, []);

  if (!user) { nav("/login"); return null; }
  if (loadErr) return <div className="page fade-up pt-6"><LoadError onRetry={load} text={loadErr} /></div>;
  if (seller === undefined) return <div className="page pt-8 text-center text-zinc-500">A carregar…</div>;
  if (seller === null) return <SellerApply onDone={load} />;

  const submit = async (pid) => {
    try { await api.post(`/products/${pid}/submit`); toast.success("Enviado para revisão"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };
  const setStatus = async (pid, status) => {
    try { await api.post(`/products/${pid}/status?status=${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setSvcStatus = async (sid, status) => {
    try { await api.post(`/services/${sid}/status?status=${status}`); toast.success(status === "published" ? "Serviço publicado" : "Estado actualizado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="page fade-up">
      <div className="pt-4 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2"><Store className="text-[#c8102e]" size={22} /><h1 className="font-display text-2xl font-extrabold">Minha loja</h1></div>
        <Link to="/vender/loja" data-testid="seller-edit-link" className="text-xs font-semibold px-3 py-2 rounded-full border border-zinc-200 hover:border-black flex items-center gap-1"><Pencil size={12} /> Editar</Link>
      </div>
      <div data-testid="seller-status-card" className="bg-white rounded-2xl border border-zinc-200 p-4 mb-4 flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-black text-white flex items-center justify-center overflow-hidden">
          {seller.logo_url ? <img src={seller.logo_url} alt="" className="w-full h-full object-cover" /> : <Store size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{seller.shop_name}</div>
          <div className="text-[11px] text-zinc-500">{seller.city} · {seller.country}</div>
        </div>
        <StatusPill status={seller.status} map={SELLER_STATUS_LABEL} />
      </div>
      {seller.status === "pending" && <Notice>A tua loja está em revisão pela ANGOLAUK+. Serás avisado quando for aprovada — depois podes publicar produtos.</Notice>}
      {seller.status === "rejected" && <Notice tone="red">A tua candidatura foi rejeitada{seller.status_reason ? `: ${seller.status_reason}` : ""}. Edita os dados e contacta support@angolauk.co.uk.</Notice>}
      {seller.status === "suspended" && <Notice tone="red">Loja suspensa{seller.status_reason ? `: ${seller.status_reason}` : ""}. Os teus produtos não estão visíveis.</Notice>}

      {seller.status === "approved" && (
        <>
          <SubscriptionCard onChange={(m) => setSubActive(!!m?.active)} />
          <SellerChecklist />
          <VerificationCard locked={!subActive} />
          <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none mb-3">
            <button onClick={() => setTab("products")} data-testid="seller-tab-products" className="chip whitespace-nowrap" data-active={tab === "products"}><Package size={12} className="inline mr-1" />Produtos ({products.length})</button>
            <button onClick={() => setTab("services")} data-testid="seller-tab-services" className="chip whitespace-nowrap" data-active={tab === "services"}><Wrench size={12} className="inline mr-1" />Serviços ({services.length})</button>
            <button onClick={() => setTab("quotes")} data-testid="seller-tab-quotes" className="chip whitespace-nowrap" data-active={tab === "quotes"}><FileText size={12} className="inline mr-1" />Orçamentos</button>
            <button onClick={() => setTab("sales")} data-testid="seller-tab-sales" className="chip whitespace-nowrap" data-active={tab === "sales"}><Receipt size={12} className="inline mr-1" />Vendas ({(sales ?? []).length})</button>
            <button onClick={() => setTab("restaurant")} data-testid="seller-tab-restaurant" className="chip whitespace-nowrap" data-active={tab === "restaurant"}><Utensils size={12} className="inline mr-1" />{seller.kind === "restaurant" ? "Menu & Horários" : "Restaurante"}</button>
            {seller.kind === "restaurant" && <button onClick={() => setTab("reservations")} data-testid="seller-tab-reservations" className="chip whitespace-nowrap" data-active={tab === "reservations"}>Reservas</button>}
            <button onClick={() => setTab("reviews")} data-testid="seller-tab-reviews" className="chip whitespace-nowrap" data-active={tab === "reviews"}><Star size={12} className="inline mr-1" />Avaliações{seller.rating_count ? ` (${seller.rating_count})` : ""}</button>
            <button onClick={() => setTab("directory")} data-testid="seller-tab-directory" className="chip whitespace-nowrap" data-active={tab === "directory"}><Compass size={12} className="inline mr-1" />Perfil público</button>
            <button onClick={() => setTab("insights")} data-testid="seller-tab-insights" className="chip whitespace-nowrap" data-active={tab === "insights"}><BarChart3 size={12} className="inline mr-1" />Insights</button>
          </div>
          {tab === "insights" && <SellerInsights />}
          {tab === "directory" && <DirectoryForm seller={seller} onSaved={load} />}
          {tab === "restaurant" && <><RestaurantSettings seller={seller} locked={!subActive} onSaved={load} />{seller.kind === "restaurant" && <MenuEditor locked={!subActive} />}</>}
          {tab === "reservations" && <RestaurantReservations />}
          {tab === "reviews" && <SellerReviews seller={seller} />}
          {tab === "services" && (
            <>
              {subActive ? (
                <Link to="/vender/servico/novo" data-testid="seller-new-service" className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] text-white font-semibold text-sm mb-3"><Plus size={16} /> Criar serviço</Link>
              ) : (
                <div data-testid="seller-locked-notice-services" className="mb-3 p-3 rounded-xl bg-zinc-50 border border-zinc-200 text-xs text-zinc-700">
                  Criar, editar e publicar serviços requer uma subscrição de lojista activa. Os teus serviços ficam guardados.
                  <PlansLink testid="seller-locked-plans-services" />
                </div>
              )}
              {services.length === 0 && <p className="text-sm text-zinc-500 text-center py-8">Ainda não tens serviços. Cria o primeiro — preço fixo ou por orçamento.</p>}
              <div className="space-y-2 mb-8">
                {services.map(s => (
                  <div key={s.id} data-testid={`my-service-${s.id}`} className="bg-white rounded-2xl border border-zinc-200 p-3 flex gap-3">
                    <div className="w-16 h-16 rounded-lg bg-zinc-100 overflow-hidden shrink-0">{s.images?.[0] && <img src={s.images[0]} alt="" className="w-full h-full object-cover" />}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{s.title}</div>
                      <div className="text-[11px] text-zinc-500">{s.service_type === "fixed_price" ? money(s.price_minor, s.currency) : "Sob consulta (orçamento)"} · {s.category}</div>
                      <div className="mt-1"><StatusPill status={s.status} map={SERVICE_STATUS_LABEL} /></div>
                      {subActive && <div className="flex flex-wrap gap-1 mt-2">
                        <Link to={`/vender/servico/${s.id}`} className="sbtn"><Pencil size={11} /> Editar</Link>
                        {["draft", "paused"].includes(s.status) && <button data-testid={`publish-service-${s.id}`} onClick={() => setSvcStatus(s.id, "published")} className="sbtn !bg-[#c8102e]"><Send size={11} /> Publicar</button>}
                        {s.status === "published" && <button data-testid={`pause-service-${s.id}`} onClick={() => setSvcStatus(s.id, "paused")} className="sbtn">Pausar</button>}
                        {s.status !== "archived" && <button onClick={() => setSvcStatus(s.id, "archived")} className="sbtn !bg-zinc-500"><Archive size={11} /> Arquivar</button>}
                      </div>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {tab === "quotes" && <SellerQuotes locked={!subActive} />}
          {tab === "products" && (
            <>
              {subActive ? (
                <Link to="/vender/produto/novo" data-testid="seller-new-product" className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] text-white font-semibold text-sm mb-3"><Plus size={16} /> Novo produto</Link>
              ) : (
                <div data-testid="seller-locked-notice" className="mb-3 p-3 rounded-xl bg-zinc-50 border border-zinc-200 text-xs text-zinc-700">
                  Publicar e editar produtos requer uma subscrição de lojista activa. Os teus produtos e a tua loja ficam guardados.
                  <PlansLink testid="seller-locked-plans" />
                </div>
              )}
              {products.length === 0 && <p className="text-sm text-zinc-500 text-center py-8">Ainda não tens produtos. Cria o primeiro!</p>}
              <div className="space-y-2 mb-8">
                {products.map(p => (
                  <div key={p.id} data-testid={`my-product-${p.id}`} className="bg-white rounded-2xl border border-zinc-200 p-3 flex gap-3">
                    <div className="w-16 h-16 rounded-lg bg-zinc-100 overflow-hidden shrink-0">{p.photos?.[0] && <img src={p.photos[0]} alt="" className="w-full h-full object-cover" />}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{p.name}</div>
                      <div className="text-[11px] text-zinc-500">{money(p.price_minor, p.currency)} · stock {p.stock}</div>
                      <div className="mt-1 flex items-center gap-1 flex-wrap"><StatusPill status={p.status} />
                        {p.flags?.length > 0 && p.status === "pending_approval" && <span className="text-[10px] text-orange-700">sinalizado: {p.flags.join(", ")}</span>}
                      </div>
                      {p.rejection_reason && <div className="text-[11px] text-red-700 mt-1">{p.rejection_reason}</div>}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {subActive && <Link to={`/vender/produto/${p.id}`} className="sbtn"><Pencil size={11} /> Editar</Link>}
                        {subActive && ["draft", "rejected"].includes(p.status) && <button data-testid={`submit-product-${p.id}`} onClick={() => submit(p.id)} className="sbtn !bg-[#c8102e]"><Send size={11} /> Submeter</button>}
                        {subActive && p.status === "published" && <button onClick={() => setStatus(p.id, "sold_out")} className="sbtn">Esgotado</button>}
                        {subActive && p.status === "sold_out" && <button onClick={() => setStatus(p.id, "published")} className="sbtn">Republicar</button>}
                        {subActive && p.status !== "archived" && <button onClick={() => setStatus(p.id, "archived")} className="sbtn !bg-zinc-500"><Archive size={11} /> Arquivar</button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {tab === "sales" && (
            <>
              <StripeConnectCard />
              {sales === null ? <LoadError onRetry={load} text="Não foi possível carregar as vendas." /> : <SalesList sales={sales} reload={load} />}
            </>
          )}
        </>
      )}
      <style>{`.sbtn{display:inline-flex;align-items:center;gap:.25rem;font-size:11px;font-weight:600;padding:.35rem .6rem;border-radius:.5rem;background:#18181b;color:#fff}.sbtn:hover{opacity:.85}`}</style>
    </div>
  );
}

function Notice({ children, tone = "yellow" }) {
  const cls = tone === "red" ? "bg-red-50 border-red-200 text-red-800" : "bg-yellow-50 border-yellow-300 text-yellow-900";
  return <div data-testid="seller-notice" className={`border rounded-2xl p-3 text-xs leading-relaxed mb-4 flex gap-2 ${cls}`}><Info size={14} className="shrink-0 mt-0.5" />{children}</div>;
}

export function SellerApply({ onDone, editing }) {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const fromBiz = params.get("from_business");
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    shop_name: "", legal_name: "", description: "", logo_url: "", photos: [], country: "GB", region: "", city: "", postcode: "",
    currency: "GBP", contacts: { phone: "", whatsapp: "", email: "", website: "" }, categories: [], delivery_methods: ["pickup"], from_business_id: fromBiz || null,
  });
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  useEffect(() => {
    api.get("/marketplace/categories", { params: { kind: "business" } }).then(r => setCats(r.data.filter(c => !/^(sub qa|qa_)/i.test(c.name))));
    if (editing) api.get("/sellers/me").then(r => r.data && setF(x => ({ ...x, ...r.data, contacts: { ...x.contacts, ...(r.data.contacts || {}) } })));
    else if (fromBiz) api.get(`/businesses/${fromBiz}`).then(r => {
      const b = r.data;
      setF(x => ({ ...x, shop_name: b.name || "", description: b.description || "", city: b.city || "", logo_url: b.cover_url || "",
        contacts: { phone: b.phone || "", whatsapp: b.whatsapp || "", email: "", website: b.website || "" }, categories: b.category ? [b.category] : [] }));
    }).catch(() => {});
  }, [editing, fromBiz]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, legal_name: f.legal_name || null, region: f.region || null, postcode: f.postcode || null, logo_url: f.logo_url || null };
      if (editing) { await api.patch("/sellers/me", body); toast.success("Loja actualizada"); nav("/vender"); }
      else { await api.post("/sellers", body); toast.success("Candidatura enviada para revisão"); onDone?.(); }
    } catch (err) { console.error("seller form submit failed", err?.response?.status, err?.response?.data); toast.error(describeError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="page fade-up">
      <div className="pt-4 pb-3 flex items-center gap-2"><Store className="text-[#c8102e]" size={22} /><h1 className="font-display text-2xl font-extrabold">{editing ? "Editar loja" : "Quero vender"}</h1></div>
      {!editing && <p className="text-sm text-zinc-600 mb-4">Cria a tua loja na ANGOLAUK+. Depois da aprovação podes publicar produtos. Só mostramos cidade/região, nunca a tua morada.</p>}
      <form onSubmit={save} className="bg-white rounded-2xl border border-zinc-200 p-4 space-y-3 mb-8">
        <L label="Nome da loja *"><input data-testid="seller-shop-name" required className={inputCls} value={f.shop_name} onChange={e => set("shop_name", e.target.value)} /></L>
        <L label="Nome legal (se aplicável)"><input data-testid="seller-legal-name" className={inputCls} value={f.legal_name || ""} onChange={e => set("legal_name", e.target.value)} /></L>
        <L label="Descrição *"><textarea data-testid="seller-description" required rows={3} className={inputCls} value={f.description} onChange={e => set("description", e.target.value)} placeholder="O que vendes, para quem, o que te distingue…" /></L>
        <L label="Logótipo"><ImageUploader testid="seller-logo" value={f.logo_url || ""} onChange={v => set("logo_url", v)} /></L>
        <div className="grid grid-cols-2 gap-2">
          <L label="País *"><select data-testid="seller-country" className={inputCls} value={f.country} onChange={e => set("country", e.target.value)}>
            {[["GB", "Reino Unido"], ["PT", "Portugal"], ["AO", "Angola"], ["FR", "França"], ["US", "EUA"], ["ZA", "África do Sul"]].map(([c, n]) => <option key={c} value={c}>{n}</option>)}</select></L>
          <L label="Moeda"><select className={inputCls} value={f.currency} onChange={e => set("currency", e.target.value)}>{["GBP", "EUR", "USD", "AOA", "ZAR"].map(c => <option key={c}>{c}</option>)}</select></L>
          <L label="Cidade *"><input data-testid="seller-city" required className={inputCls} value={f.city} onChange={e => set("city", e.target.value)} placeholder="London" /></L>
          <L label="Região"><input className={inputCls} value={f.region || ""} onChange={e => set("region", e.target.value)} placeholder="Greater London" /></L>
          <L label="Código postal (zona)"><input className={inputCls} value={f.postcode || ""} onChange={e => set("postcode", e.target.value)} placeholder="SE5" /></L>
        </div>
        <L label="Contactos">
          <div className="grid grid-cols-2 gap-2">
            {["phone", "whatsapp", "email", "website"].map(k => <input key={k} data-testid={`seller-contact-${k}`} className={inputCls} placeholder={k} value={f.contacts?.[k] || ""} onChange={e => set("contacts", { ...f.contacts, [k]: e.target.value })} />)}
          </div>
        </L>
        <L label="Categorias">
          <div className="space-y-2" data-testid="seller-categories">{cats.filter(c => !c.parent_id).map(parent => {
            const group = [parent, ...cats.filter(c => c.parent_id === parent.id)];
            return (
              <div key={parent.id}>
                <div className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">{parent.name}</div>
                <div className="flex flex-wrap gap-1.5">{group.map(c => <button type="button" key={c.id} data-testid={`seller-cat-${c.id}`} className="chip !text-[11px]" data-active={f.categories.includes(c.name)}
                  onClick={() => set("categories", f.categories.includes(c.name) ? f.categories.filter(x => x !== c.name) : [...f.categories, c.name])}>{c.name}</button>)}</div>
              </div>);
          })}</div>
        </L>
        <L label="Métodos de entrega *">
          <div className="flex gap-1.5">{[["delivery", "Entrega"], ["pickup", "Levantamento"]].map(([k, n]) => <button type="button" key={k} data-testid={`seller-dm-${k}`} className="chip !text-[11px]" data-active={f.delivery_methods.includes(k)}
            onClick={() => set("delivery_methods", f.delivery_methods.includes(k) ? f.delivery_methods.filter(x => x !== k) : [...f.delivery_methods, k])}>{n}</button>)}</div>
        </L>
        <button data-testid="seller-submit" disabled={busy || f.delivery_methods.length === 0} className="w-full py-3 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] disabled:opacity-50 text-white font-semibold text-sm">
          {busy ? "A guardar…" : editing ? "Guardar alterações" : "Submeter candidatura"}
        </button>
      </form>
    </div>
  );
}

export function L({ label, children }) {
  return <label className="block"><div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">{label}</div>{children}</label>;
}
